import { Component, Mixins, Prop } from 'vue-property-decorator'
import { mapState } from 'vuex'
import { NumberMixin } from '@kwixeo/interface-vue'
import mapValues from 'lodash/mapValues'
import ColorsMixin from './ColorsMixin'

@Component({
  computed: {
    ...mapState('dashboard', ['widgets'])
  }
})
export default class WidgetMixin extends Mixins(NumberMixin, ColorsMixin) {
  widgets!: KwixeoDashboard.AvailableWidgets

  @Prop({ required: true }) value!: KwixeoDashboard.WidgetNumber|KwixeoDashboard.WidgetDiagram|KwixeoDashboard.WidgetBar
  @Prop({ required: true }) datas!: KwixeoDashboard.DashboardDatas
  @Prop({ required: true }) compare!: boolean
  @Prop({ required: true }) initialized!: boolean

  protected colors = [
    '#f02b2b', '#22c940', '#2865de', '#e17921',
    '#eae62a', '#e74b4b'
  ]

  get computedCompare (): boolean {
    if (this.value.params.period !== 'default') {
      return false
    }
    return this.compare
  }

  get dataKey (): string {
    const params = this.value.params
    let key = params.key

    if ('limit' in params) {
      key += '-limit-' + params.limit
    }

    if (params.filters) {
      mapValues(params.filters, ((filter, index) => {
        key += '-' + index + '-' + filter.join('-')
      }))
    }

    if (params.period) {
      key += '-' + params.period
      if (params.period !== 'default') {
        key += '-' + params.period_value + '-' + params.period_type
      }
    }

    return key
  }

  get labels (): string[] {
    const datas = this.dashboardData as KwixeoDashboard.BarDatas
    return datas.map(data => data.label as string)
  }

  get widget (): KwixeoDashboard.AvailableWidget {
    return this.widgets.find(widget => widget.id === this.value.params.key) as KwixeoDashboard.AvailableWidget
  }

  get dashboardData (): KwixeoDashboard.DiagramDatas|KwixeoDashboard.BarDatas|KwixeoDashboard.NumberDatas|KwixeoDashboard.ListDatas {
    const key = this.dataKey
    if (!key) {
      return {}
    }
    return this.datas[key] as (KwixeoDashboard.DiagramDatas|KwixeoDashboard.BarDatas|KwixeoDashboard.NumberDatas)
  }

  get dashboardDataCompare (): KwixeoDashboard.DiagramDatas|KwixeoDashboard.BarDatas|KwixeoDashboard.NumberDatas {
    const key = this.dataKey
    if (!key) {
      return {}
    }
    return this.datas['compare-' + key] as (KwixeoDashboard.DiagramDatas|KwixeoDashboard.BarDatas|KwixeoDashboard.NumberDatas)
  }

  get dataColors (): string[] {
    const datas = this.dashboardData as (KwixeoDashboard.BarDatas|KwixeoDashboard.DiagramDatas)
    return datas.map((data, index) => {
      return this.colors[index] ?? this.getRandomColor()
    })
  }

  get tooltipLabel (): (value: number, datas: number[], compareValue?: null|number) => string[] {
    return (value, datas, compareValue = null) => {
      const params = this.value.params as KwixeoDashboard.DiagramParams
      const data = this.widget.datas.find(data => data.key === params.value) as KwixeoDashboard.AvailableWidgetData

      let sum = 0
      datas.map(data => sum += data)
      const percentage = (value*100 / sum).toFixed(2) + '%'

      const labels = [
        data.label + (this.compare && compareValue === null ? ' N-1' : ''),
        this.formattedNumber(value, data.key === 'count' ? false : null) + ' (' + percentage + ')',
      ]

      if (this.compare && compareValue !== null) {
        const percentage = (value*100 / compareValue).toFixed(2) + '%'

        labels.push(...[
          'N-1 : +' + percentage,
          ''
        ])
      }

      return labels
    }
  }

  protected getRandomColor (): string {
    const letters = '0123456789ABCDEF'.split('')
    let color = '#'
    for (let i = 0; i < 6; i++ ) {
      color += letters[Math.floor(Math.random() * 16)]
    }
    return color
  }
}
